import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { isClaimedAction } from "@/lib/billingConstants";

export const REPORT_TYPES = [
  { value: "overview", label: "Overview" },
  { value: "fees-week", label: "Fees by week" },
  { value: "type", label: "By appearance type" },
  { value: "fees-lawyer", label: "Fees by lawyer" },
  { value: "unclaimed", label: "Unclaimed money" },
];

// Appearances that will never be claimed from VLA, so their fee is not money owed.
const NOT_CLAIMABLE = ["Not claimed", "Admin Adjourned", "Counsel appeared", "LACW funded"];

const isClaimed = (entry) => isClaimedAction(entry.action);
const isOutstanding = (entry) => entry.fee != null && !isClaimed(entry) && !NOT_CLAIMABLE.includes(entry.action);

export function formatMoney(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

// "28/09/2026" → sortable number; undated entries sort last.
function dateSortValue(value = "") {
  const match = String(value).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
  return year * 10000 + Number(match[2]) * 100 + Number(match[1]);
}

function groupEntries(entries, keyOf) {
  const groups = new Map();
  entries.forEach((entry) => {
    const label = keyOf(entry);
    const row = groups.get(label) || { label, count: 0, claimed: 0, outstanding: 0, total: 0, noFee: 0 };
    row.count += 1;
    if (entry.fee == null) row.noFee += 1;
    else if (isClaimed(entry)) row.claimed += entry.fee;
    else if (isOutstanding(entry)) row.outstanding += entry.fee;
    row.total = row.claimed + row.outstanding;
    groups.set(label, row);
  });
  return Array.from(groups.values());
}

export function buildBillingReport(reportType, entries, weekOrder = []) {
  if (reportType === "unclaimed") {
    const list = entries
      .filter(isOutstanding)
      .sort((a, b) => dateSortValue(a.date) - dateSortValue(b.date));
    return {
      type: "list",
      title: "Unclaimed money",
      description: "Appearances with a fee that have not been claimed yet (no appearance type, Claimed or Billed and closed set). Not claimed, Counsel appeared and LACW funded are left out.",
      entries: list,
      total: list.reduce((sum, entry) => sum + entry.fee, 0),
    };
  }

  if (reportType === "fees-week") {
    const rows = groupEntries(entries, (e) => e.weekLabel || "Unnamed Week");
    const order = (label) => {
      const index = weekOrder.indexOf(label);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    return { type: "grouped", title: "Fees by week", groupLabel: "Week", rows: rows.sort((a, b) => order(a.label) - order(b.label)) };
  }

  if (reportType === "type") {
    const rows = groupEntries(entries, (e) => e.billingType || "Not recorded");
    return { type: "grouped", title: "By appearance type", groupLabel: "Appearance type", rows: rows.sort((a, b) => b.total - a.total || b.count - a.count) };
  }

  if (reportType === "fees-lawyer") {
    const rows = groupEntries(entries, (e) => (e.lawyer_initials || "Unknown").trim().toUpperCase());
    return { type: "grouped", title: "Fees by lawyer", groupLabel: "Lawyer", rows: rows.sort((a, b) => b.total - a.total || b.count - a.count) };
  }

  return { type: "overview" };
}

function SummaryTile({ label, value, tone }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-black ${tone}`}>{value}</p>
    </div>
  );
}

export default function BillingReportPanel({ report, onOpenWeek }) {
  if (report.type === "list") {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SummaryTile label="Still to claim" value={formatMoney(report.total)} tone="text-amber-600" />
          <SummaryTile label="Appearances" value={report.entries.length} tone="text-slate-800 dark:text-white" />
        </div>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="border-b border-slate-100 p-5 dark:border-gray-700">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">{report.title}</h3>
            <p className="mt-1 text-xs text-slate-400">{report.description}</p>
          </div>
          {report.entries.length === 0 ? (
            <div className="p-12 text-center text-xs text-slate-400">Nothing left to claim for these filters</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:border-gray-700 dark:bg-gray-700 dark:text-slate-400">
                    <th className="p-3">Date</th>
                    <th className="p-3">Week</th>
                    <th className="p-3">Client</th>
                    <th className="p-3">Appearance type</th>
                    <th className="p-3">Lawyer</th>
                    <th className="p-3">Action</th>
                    <th className="p-3 text-right">Fee</th>
                    <th className="p-3">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                  {report.entries.map((e, index) => (
                    <tr key={`${e.weekLabel}-${e.id || index}`}>
                      <td className="whitespace-nowrap p-3 font-semibold text-slate-600 dark:text-slate-300">{e.date}</td>
                      <td className="p-3">
                        <button onClick={() => onOpenWeek?.(e.weekLabel)} className="whitespace-nowrap text-left font-bold text-blue-600 hover:underline dark:text-blue-400" title="Open this week's billing sheet">
                          {e.weekLabel}
                        </button>
                      </td>
                      <td className="p-3 font-bold text-slate-800 dark:text-slate-200">{e.client_name}</td>
                      <td className="whitespace-nowrap p-3 text-slate-600 dark:text-slate-300">{e.billingType}</td>
                      <td className="p-3 font-bold text-slate-600 dark:text-slate-400">{e.lawyer_initials || "—"}</td>
                      <td className="whitespace-nowrap p-3 text-slate-600 dark:text-slate-300">{e.action}</td>
                      <td className="whitespace-nowrap p-3 text-right font-semibold text-slate-800 dark:text-slate-200">{formatMoney(e.fee)}</td>
                      <td className="max-w-[220px] truncate p-3 text-slate-500 dark:text-slate-400" title={e.note}>{e.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  const totals = report.rows.reduce(
    (sum, row) => ({ claimed: sum.claimed + row.claimed, outstanding: sum.outstanding + row.outstanding, count: sum.count + row.count, noFee: sum.noFee + row.noFee }),
    { claimed: 0, outstanding: 0, count: 0, noFee: 0 }
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryTile label="Claimed" value={formatMoney(totals.claimed)} tone="text-emerald-600" />
        <SummaryTile label="Still to claim" value={formatMoney(totals.outstanding)} tone="text-amber-600" />
        <SummaryTile label="Appearances" value={totals.count} tone="text-slate-800 dark:text-white" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-4 text-sm font-bold text-slate-700 dark:text-slate-200">{report.title}</h3>
        {report.rows.length === 0 ? (
          <div className="flex h-[260px] items-center justify-center text-xs text-slate-400">No data available</div>
        ) : (
          <div className="w-full" style={{ height: Math.max(220, Math.min(report.rows.length, 12) * 34 + 60) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={report.rows.slice(0, 12)} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={(v) => `$${v}`} />
                <YAxis dataKey="label" type="category" width={130} tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(value) => formatMoney(value)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="claimed" name="Claimed" stackId="fees" fill="#10b981" />
                <Bar dataKey="outstanding" name="Still to claim" stackId="fees" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {report.rows.length > 12 && <p className="mt-2 text-[11px] text-slate-400">Chart shows the top 12; the table lists all {report.rows.length}.</p>}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:border-gray-700 dark:bg-gray-700 dark:text-slate-400">
                <th className="p-3">{report.groupLabel}</th>
                <th className="p-3 text-right">Appearances</th>
                <th className="p-3 text-right">Claimed</th>
                <th className="p-3 text-right">Still to claim</th>
                <th className="p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
              {report.rows.map((row) => (
                <tr key={row.label}>
                  <td className="p-3 font-bold text-slate-800 dark:text-slate-200">
                    {report.groupLabel === "Week" && onOpenWeek ? (
                      <button onClick={() => onOpenWeek(row.label)} className="text-left text-blue-600 hover:underline dark:text-blue-400" title="Open this week's billing sheet">{row.label}</button>
                    ) : row.label}
                  </td>
                  <td className="p-3 text-right text-slate-600 dark:text-slate-300">
                    {row.count}
                    {row.noFee > 0 && <span className="ml-1 text-[10px] text-slate-400">({row.noFee} no fee)</span>}
                  </td>
                  <td className="p-3 text-right font-semibold text-emerald-700 dark:text-emerald-400">{formatMoney(row.claimed)}</td>
                  <td className="p-3 text-right font-semibold text-amber-700 dark:text-amber-400">{formatMoney(row.outstanding)}</td>
                  <td className="p-3 text-right font-bold text-slate-800 dark:text-slate-200">{formatMoney(row.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold dark:border-gray-600 dark:bg-gray-700">
                <td className="p-3 text-slate-800 dark:text-slate-200">Total</td>
                <td className="p-3 text-right text-slate-700 dark:text-slate-300">{totals.count}</td>
                <td className="p-3 text-right text-emerald-700 dark:text-emerald-400">{formatMoney(totals.claimed)}</td>
                <td className="p-3 text-right text-amber-700 dark:text-amber-400">{formatMoney(totals.outstanding)}</td>
                <td className="p-3 text-right text-slate-800 dark:text-slate-200">{formatMoney(totals.claimed + totals.outstanding)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {totals.noFee > 0 && (
        <p className="text-[11px] text-slate-400">{totals.noFee} appearance{totals.noFee === 1 ? " has" : "s have"} no listed fee (for example the court or type isn't in the VLA table) and {totals.noFee === 1 ? "isn't" : "aren't"} counted in the dollar totals.</p>
      )}
    </div>
  );
}
